/**
 * Persistenza della richiesta di attivazione (claim) dealer in corso.
 *
 * Serve a riprendere il flusso dopo il magic link: l'utente verifica l'OTP,
 * riceve l'email di accesso e torna sul sito in una scheda diversa. Senza
 * questa persistenza il claim_id restava solo nello stato React e la
 * richiesta rimaneva "verified" ma mai completata.
 *
 * Chiave essenziale (funzionale al servizio richiesto dall'utente).
 */
import { safeLocalStorage } from '@/utils/safeStorage';

const STORAGE_KEY = 'gildy_pending_dealer_claim';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 giorni

export interface PendingDealerClaim {
  claimId: string;
  dealerId: string;
  dealerName?: string;
  citySlug?: string;
  savedAt: number;
}

export function savePendingClaim(claim: Omit<PendingDealerClaim, 'savedAt'>): void {
  try {
    safeLocalStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...claim, savedAt: Date.now() } satisfies PendingDealerClaim),
      true // essenziale: senza questo dato l'attivazione richiesta non può concludersi
    );
  } catch {
    /* noop */
  }
}

export function getPendingClaim(): PendingDealerClaim | null {
  try {
    const raw = safeLocalStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingDealerClaim;
    if (!parsed?.claimId || !parsed?.dealerId) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      clearPendingClaim();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingClaim(): void {
  try {
    safeLocalStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/** URL di ritorno del magic link, con contesto minimo nel query string come fallback */
export function buildClaimResumeUrl(claim: Omit<PendingDealerClaim, 'savedAt'>): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.gildy.it';
  const params = new URLSearchParams({ claim: claim.claimId, d: claim.dealerId });
  if (claim.citySlug) params.set('c', claim.citySlug);
  return `${origin}/attiva/completa?${params.toString()}`;
}
